import { ReactNode, useMemo } from 'react';
import { BreadcrumbItem } from '../hooks/useBreadcrumb';
import { useBreadcrumbLabels } from './breadcrumbLocalization';
import { paths } from 'routes/paths';

/**
 * Localized labels interface for breadcrumbs
 */
export interface BreadcrumbLabels {
  home: string;
  knowledgeBase: string;
  pipeline: string;
  documents: string;
  [key: string]: string;
}

/**
 * Context object for dynamic breadcrumb generation
 */
export interface BreadcrumbContext {
  /** Name or label for dynamic items */
  catalogName?: string;
  /** Catalog ID for generating links */
  catalogId?: string;
  /** Document name for URL paths breadcrumb */
  documentName?: string;
  /** Fallback text when dynamic content is not available */
  fallbackText?: string;
  /** Loading state for dynamic content */
  isLoading?: boolean;
  /** Edit handler function that accepts the new name */
  editHandler?: (newName: string) => void | Promise<void>;
  /** Skeleton element to show when loading */
  skeletonElement?: ReactNode;
  /** Localized labels for static breadcrumb items */
  labels?: BreadcrumbLabels;
}

/**
 * Breadcrumb configuration for different page types
 */
export interface BreadcrumbConfig {
  /** Static breadcrumb items that don't change */
  staticItems?: (labels: BreadcrumbLabels) => BreadcrumbItem[];
  /** Dynamic last item configuration */
  dynamicLastItem?: {
    /** Function to get the label for the last item */
    getLabel: (context: BreadcrumbContext) => ReactNode;
    /** Function to determine if the last item is editable */
    isEditable?: (context: BreadcrumbContext) => boolean;
    /** Function to get the edit handler for the last item */
    getEditHandler?: (context: BreadcrumbContext) => ((newName: string) => void | Promise<void>) | undefined;
  };
}

/**
 * Predefined breadcrumb configurations for common page patterns
 */
export const BREADCRUMB_CONFIGS: Record<string, BreadcrumbConfig> = {
  // Home page - just the home item
  home: {
    staticItems: (labels) => [{ label: labels.home }],
  },

  // Knowledge Base main page
  knowledgeBase: {
    staticItems: (labels) => [{ label: labels.home, path: '/' }, { label: labels.knowledgeBase }],
  },

  // Knowledge Base Documents page - dynamic catalog name
  knowledgeBaseDocuments: {
    staticItems: (labels) => [
      { label: labels.home, path: '/' },
      { label: labels.knowledgeBase, path: paths.knowledgeBase },
    ],
    dynamicLastItem: {
      getLabel: (context) => {
        // The component should pass the skeleton as skeletonElement when loading
        if (context.isLoading && context.skeletonElement) {
          return context.skeletonElement;
        }
        return context.catalogName;
      },
      isEditable: (context) => !!context.catalogName && !context.isLoading,
      getEditHandler: (context) => context.editHandler,
    },
  },

  // Knowledge Base URL Paths page - catalog name and document name
  knowledgeBaseUrlPaths: {
    staticItems: (labels) => [
      { label: labels.home, path: '/' },
      { label: labels.knowledgeBase, path: paths.knowledgeBase },
    ],
    dynamicLastItem: {
      getLabel: (context) => {
        if (context.isLoading && context.skeletonElement) {
          return context.skeletonElement;
        }
        return context.documentName;
      },
      isEditable: () => false, // Document name is not editable as per requirements
    },
  },

  // Pipeline main page
  pipeline: {
    staticItems: (labels) => [{ label: labels.home, path: '/' }, { label: labels.pipeline }],
  },

  // Pipeline Documents page - dynamic catalog name
  pipelineDocuments: {
    staticItems: (labels) => [
      { label: labels.home, path: '/' },
      { label: labels.pipeline, path: paths.pipeline },
    ],
    dynamicLastItem: {
      getLabel: (context) => {
        // The component should pass the skeleton as skeletonElement when loading
        if (context.isLoading && context.skeletonElement) {
          return context.skeletonElement;
        }
        return context.catalogName;
      },
      isEditable: (context) => !!context.catalogName && !context.isLoading,
      getEditHandler: (context) => context.editHandler,
    },
  },

  // Pipeline URL Paths page - catalog name and document name
  pipelineUrlPaths: {
    staticItems: (labels) => [
      { label: labels.home, path: '/' },
      { label: labels.pipeline, path: paths.pipeline },
    ],
    dynamicLastItem: {
      getLabel: (context) => {
        if (context.isLoading && context.skeletonElement) {
          return context.skeletonElement;
        }
        return context.documentName;
      },
      isEditable: () => false, // Document name is not editable as per requirements
    },
  },
};

/**
 * Generates breadcrumb items based on a configuration and context
 */
export function generateBreadcrumbs(
  configKey: string,
  context: BreadcrumbContext = {},
): {
  breadcrumbItems: BreadcrumbItem[];
  lastItemEditable: boolean;
  onEditLastItem?: (newName: string) => void | Promise<void>;
} {
  const config = BREADCRUMB_CONFIGS[configKey];

  if (!config) {
    throw new Error(`Breadcrumb configuration not found for key: ${configKey}`);
  }

  // Provide default labels if not provided
  const defaultLabels: BreadcrumbLabels = {
    home: 'Home',
    knowledgeBase: 'Knowledge Base',
    pipeline: 'Pipeline',
    documents: 'Documents',
  };

  const labels = context.labels || defaultLabels;
  const breadcrumbItems: BreadcrumbItem[] = config.staticItems ? config.staticItems(labels) : [];
  let lastItemEditable = false;
  let onEditLastItem: ((newName: string) => void | Promise<void>) | undefined;

  // Special handling for URL paths - insert catalog item before the last item
  if (configKey === 'knowledgeBaseUrlPaths' && context.catalogName) {
    // Add catalog item with link to documents page
    const catalogPath = context.catalogId ? `/knowledge-base/${context.catalogId}/documents` : undefined;
    breadcrumbItems.push({ label: context.catalogName, path: catalogPath });
  }
  
  if (configKey === 'pipelineUrlPaths' && context.catalogName) {
    // Add catalog item with link to documents page
    const catalogPath = context.catalogId ? `/pipeline/${context.catalogId}/documents` : undefined;
    breadcrumbItems.push({ label: context.catalogName, path: catalogPath });
  }

  // Add dynamic last item if configured
  if (config.dynamicLastItem) {
    const label = config.dynamicLastItem.getLabel(context);
    breadcrumbItems.push({ label });

    lastItemEditable = config.dynamicLastItem.isEditable?.(context) || false;
    onEditLastItem = config.dynamicLastItem.getEditHandler?.(context);
  }

  return {
    breadcrumbItems,
    lastItemEditable,
    onEditLastItem,
  };
}

/**
 * Auto-detects breadcrumb configuration based on the current route path
 */
export function detectBreadcrumbConfig(pathname: string): string | null {
  // Remove query parameters and trailing slashes
  const cleanPath = pathname.split('?')[0].replace(/\/$/, '');

  // Knowledge Base Documents pattern: /knowledge-base/:catalogId/documents
  if (/^\/knowledge-base\/[^/]+\/documents$/.test(cleanPath)) {
    return 'knowledgeBaseDocuments';
  }

  // Pipeline Documents pattern: /pipeline/:catalogId/documents
  if (/^\/pipeline\/[^/]+\/documents$/.test(cleanPath)) {
    return 'pipelineDocuments';
  }

  // Knowledge Base main page
  if (cleanPath === paths.knowledgeBase) {
    return 'knowledgeBase';
  }

  // Pipeline main page
  if (cleanPath === paths.pipeline) {
    return 'pipeline';
  }

  // Home
  if (cleanPath === paths.home) {
    return 'home';
  }

  return null;
}

/**
 * Hook-like function to generate breadcrumbs automatically based on the current location
 */
export function useDynamicBreadcrumbs(
  pathname: string,
  context: BreadcrumbContext = {},
): {
  breadcrumbItems: BreadcrumbItem[];
  lastItemEditable: boolean;
  onEditLastItem?: (newName: string) => void | Promise<void>;
} | null {
  const configKey = detectBreadcrumbConfig(pathname);

  if (!configKey) {
    return null;
  }

  return generateBreadcrumbs(configKey, context);
}

/**
 * Hook to generate localized breadcrumbs
 */
export function useLocalizedBreadcrumbs(
  configKey: string,
  context: Omit<BreadcrumbContext, 'labels'> = {},
): {
  breadcrumbItems: BreadcrumbItem[];
  lastItemEditable: boolean;
  onEditLastItem?: (newName: string) => void | Promise<void>;
} | null {
  const labels = useBreadcrumbLabels();

  // Create a stable context object by extracting dependencies
  const { catalogName, catalogId, documentName, fallbackText, isLoading, editHandler, skeletonElement } = context;

  return useMemo(() => {
    if (!configKey) {
      return null;
    }

    // Reconstruct context object inside useMemo to ensure stable reference
    const contextWithLabels: BreadcrumbContext = {
      catalogName,
      catalogId,
      documentName,
      fallbackText,
      isLoading,
      editHandler,
      skeletonElement,
      labels,
    };

    return generateBreadcrumbs(configKey, contextWithLabels);
  }, [configKey, catalogName, catalogId, documentName, fallbackText, isLoading, editHandler, skeletonElement, labels]);
}

/**
 * Hook-like function to generate breadcrumbs automatically based on the current location with localization
 */
export function useLocalizedDynamicBreadcrumbs(
  pathname: string,
  context: Omit<BreadcrumbContext, 'labels'> = {},
): {
  breadcrumbItems: BreadcrumbItem[];
  lastItemEditable: boolean;
  onEditLastItem?: (newName: string) => void | Promise<void>;
} | null {
  const configKey = detectBreadcrumbConfig(pathname);
  const breadcrumbs = useLocalizedBreadcrumbs(configKey ?? '', context);

  if (!configKey) {
    return null;
  }

  return breadcrumbs;
}
